// pages/InvoiceList.js - Updated with Table View and Series Order

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, remove, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";
import PrintService from '../components/PrintService';

export default function InvoiceList() {
  const navigate = useNavigate();
  const printService = new PrintService();
  
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [upiDetails, setUpiDetails] = useState(null);
  const [settings, setSettings] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // table or card
  const [stats, setStats] = useState({
    totalInvoices: 0,
    totalRevenue: 0,
    totalTax: 0
  });

  // Load company details, bank details, settings
  useEffect(() => {
    // Load company details
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCompanyDetails(data);
      }
    });

    // Load bank details
    const bankRef = ref(database, "bankDetails");
    onValue(bankRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setBankDetails(data);
      }
    });

    // Load UPI details
    const upiRef = ref(database, "upiDetails");
    onValue(upiRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUpiDetails(data);
      }
    });

    // Load settings
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setSettings(data);
      }
    });
  }, []);

  // Load all financial years
  useEffect(() => {
    const financialYearsRef = ref(database, "financialYears");
    onValue(financialYearsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const years = Object.keys(data).sort().reverse();
        setFinancialYears(years);
        if (years.length > 0 && !selectedYear) {
          setSelectedYear(years[0]);
        }
      }
      setLoading(false);
    });
  }, []);

  // Load invoices for selected financial year
  useEffect(() => {
    if (!selectedYear) return;

    const billsRef = ref(database, `financialYears/${selectedYear}/bills`);
    onValue(billsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const invoiceList = Object.entries(data).map(([id, invoice]) => ({
          id,
          ...invoice
        }));
        
        // Sort by invoice number in series order
        invoiceList.sort((a, b) => {
          // Extract numbers from invoice numbers
          const aNum = extractInvoiceNumber(a.invoiceNo);
          const bNum = extractInvoiceNumber(b.invoiceNo);
          return aNum - bNum;
        });
        
        setInvoices(invoiceList);
        setFilteredInvoices(invoiceList);
        
        // Calculate stats
        const totalRevenue = invoiceList.reduce((sum, inv) => sum + (inv.grandTotal || 0), 0);
        const totalTax = invoiceList.reduce((sum, inv) => sum + (inv.totalGST || 0), 0);
        
        setStats({
          totalInvoices: invoiceList.length,
          totalRevenue,
          totalTax
        });
      } else {
        setInvoices([]);
        setFilteredInvoices([]);
        setStats({
          totalInvoices: 0,
          totalRevenue: 0,
          totalTax: 0
        });
      }
    });
  }, [selectedYear]);

  // Extract invoice number for sorting
  const extractInvoiceNumber = (invoiceNo) => {
    if (!invoiceNo) return 0;
    const match = invoiceNo.match(/\d+/);
    if (match) {
      return parseInt(match[0], 10);
    }
    return 0;
  };

  // Search functionality
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredInvoices(invoices);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = invoices.filter(invoice => {
      return (
        invoice.invoiceNo?.toLowerCase().includes(term) ||
        invoice.customerName?.toLowerCase().includes(term) ||
        invoice.customerMobile?.includes(term) ||
        invoice.customerGST?.toLowerCase().includes(term) ||
        invoice.paymentMode?.toLowerCase().includes(term) ||
        invoice.items?.some(item => 
          item.name?.toLowerCase().includes(term) ||
          item.hsn?.toLowerCase().includes(term)
        )
      );
    });
    
    setFilteredInvoices(filtered);
  }, [searchTerm, invoices]);

  const handleDelete = async (invoiceId) => {
    if (!selectedYear) return;
    
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        const invoiceRef = ref(database, `financialYears/${selectedYear}/bills/${invoiceId}`);
        await remove(invoiceRef);
        alert("Invoice deleted successfully!");
      } catch (error) {
        console.error("Error deleting invoice:", error);
        alert("Failed to delete invoice");
      }
    }
  };

  const handleEdit = (invoiceId) => {
    navigate(`/invoice/edit/${invoiceId}`);
  };

  const handleView = (invoiceId) => {
    navigate(`/invoice/view/${invoiceId}`);
  };

  const handlePrint = async (invoice) => {
    try {
      const invoiceRef = ref(database, `financialYears/${selectedYear}/bills/${invoice.id}`);
      const snapshot = await get(invoiceRef);
      
      if (snapshot.exists()) {
        const fullInvoice = snapshot.val();
        
        const printData = {
          settings: settings || {},
          company: companyDetails || {},
          selectedCustomer: {
            id: fullInvoice.customerId,
            name: fullInvoice.customerName,
            mobile: fullInvoice.customerMobile,
            gst: fullInvoice.customerGST,
            address: fullInvoice.customerAddress,
            state: fullInvoice.customerState,
            stateCode: fullInvoice.customerStateCode
          },
          invoiceItems: fullInvoice.items || [],
          totals: {
            taxableValue: fullInvoice.taxableValue || 0,
            cgst: fullInvoice.cgst || 0,
            sgst: fullInvoice.sgst || 0,
            igst: fullInvoice.igst || 0,
            totalGST: fullInvoice.totalGST || 0,
            totalDiscount: fullInvoice.totalDiscount || 0,
            grandTotal: fullInvoice.grandTotal || 0
          },
          invoiceNo: fullInvoice.invoiceNo,
          invoiceDate: fullInvoice.date,
          dueDate: fullInvoice.date,
          notes: fullInvoice.notes || "",
          terms: companyDetails?.terms || 'Thank you for your business!',
          paymentMode: fullInvoice.paymentMode,
          bankDetails: bankDetails || {},
          upiDetails: upiDetails || {}
        };

        printService.print(printData);
      }
    } catch (error) {
      console.error("Error printing invoice:", error);
      alert("Failed to print invoice");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  return (
    <div className="invoice-list-app">
      <div className="page-header">
        <h2>Invoices</h2>
        <div className="header-actions">
          <div className="view-toggle">
            <button 
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
            >
              📋 Table View
            </button>
            <button 
              className={`toggle-btn ${viewMode === "card" ? "active" : ""}`}
              onClick={() => setViewMode("card")}
            >
              🃏 Card View
            </button>
          </div>
          <button 
            className="create-btn"
            onClick={() => navigate("/invoice/new")}
          >
            + New Invoice
          </button>
        </div>
      </div>

      {/* Financial Year Selector */}
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(e.target.value)}
          className="year-select"
        >
          {financialYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <span className="invoice-range">
          {filteredInvoices.length > 0 && (
            <>Invoice Range: {filteredInvoices[0]?.invoiceNo} - {filteredInvoices[filteredInvoices.length - 1]?.invoiceNo}</>
          )}
        </span>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by invoice no., customer name, mobile, GST, items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search" onClick={clearSearch}>
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <div className="search-results-count">
            Found {filteredInvoices.length} {filteredInvoices.length === 1 ? 'invoice' : 'invoices'}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats.totalInvoices > 0 && (
        <div className="stats-cards">
          <div className="stat-card">
            <span className="stat-label">Total Invoices</span>
            <span className="stat-value">{stats.totalInvoices}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Revenue</span>
            <span className="stat-value">{formatCurrency(stats.totalRevenue)}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Total Tax</span>
            <span className="stat-value">{formatCurrency(stats.totalTax)}</span>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="table-container">
          {filteredInvoices.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? (
                <>
                  <p>No invoices found matching "{searchTerm}"</p>
                  <button className="clear-search-btn" onClick={clearSearch}>
                    Clear Search
                  </button>
                </>
              ) : (
                <>
                  <p>No invoices found for financial year {selectedYear}</p>
                  <button 
                    className="create-first-btn"
                    onClick={() => navigate("/invoice/new")}
                  >
                    Create First Invoice
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Invoice No.</th>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Customer Mobile</th>
                    <th>GSTIN</th>
                    <th>Taxable Value</th>
                    <th>GST Amount</th>
                    <th>Grand Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice, index) => (
                    <tr key={invoice.id}>
                      <td className="serial-no">{index + 1}</td>
                      <td className="invoice-no">{invoice.invoiceNo}</td>
                      <td>{formatDate(invoice.date)}</td>
                      <td className="customer-name">{invoice.customerName}</td>
                      <td>{invoice.customerMobile || "-"}</td>
                      <td>{invoice.customerGST || "-"}</td>
                      <td className="amount">{formatCurrency(invoice.taxableValue || 0)}</td>
                      <td className="amount">{formatCurrency(invoice.totalGST || 0)}</td>
                      <td className="amount grand-total">{formatCurrency(invoice.grandTotal || 0)}</td>
                      <td>
                        <span className={`status-badge ${invoice.status || "completed"}`}>
                          {invoice.status || "Completed"}
                        </span>
                      </td>
                      <td className="actions">
                        <button 
                          className="action-btn view"
                          onClick={() => handleView(invoice.id)}
                          title="View"
                        >
                          👁️
                        </button>
                        <button 
                          className="action-btn print"
                          onClick={() => handlePrint(invoice)}
                          title="Print"
                        >
                          🖨️
                        </button>
                        <button 
                          className="action-btn edit"
                          onClick={() => handleEdit(invoice.id)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDelete(invoice.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="totals-row">
                    <td colSpan="6"><strong>Totals</strong></td>
                    <td className="amount"><strong>{formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (inv.taxableValue || 0), 0))}</strong></td>
                    <td className="amount"><strong>{formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (inv.totalGST || 0), 0))}</strong></td>
                    <td className="amount"><strong>{formatCurrency(filteredInvoices.reduce((sum, inv) => sum + (inv.grandTotal || 0), 0))}</strong></td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Card View (Original Layout) */}
      {viewMode === "card" && (
        <div className="invoices-container">
          {filteredInvoices.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? (
                <>
                  <p>No invoices found matching "{searchTerm}"</p>
                  <button className="clear-search-btn" onClick={clearSearch}>
                    Clear Search
                  </button>
                </>
              ) : (
                <>
                  <p>No invoices found for financial year {selectedYear}</p>
                  <button 
                    className="create-first-btn"
                    onClick={() => navigate("/invoice/new")}
                  >
                    Create First Invoice
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="invoice-cards">
              {filteredInvoices.map(invoice => (
                <div key={invoice.id} className="invoice-card">
                  <div className="invoice-card-header">
                    <span className="invoice-no">{invoice.invoiceNo}</span>
                    <span className={`payment-mode ${invoice.paymentMode}`}>
                      {invoice.paymentMode?.toUpperCase() || "N/A"}
                    </span>
                  </div>
                  
                  <div className="invoice-card-body">
                    <div className="customer-info">
                      <strong>{invoice.customerName}</strong>
                      {invoice.customerMobile && (
                        <span>{invoice.customerMobile}</span>
                      )}
                    </div>
                    
                    <div className="invoice-details">
                      <span>📅 {formatDate(invoice.date)}</span>
                      <span className="amount">💰 {formatCurrency(invoice.grandTotal)}</span>
                    </div>
                    
                    {invoice.items && (
                      <div className="items-count">
                        {invoice.items.length} {invoice.items.length === 1 ? 'item' : 'items'}
                      </div>
                    )}
                  </div>
                  
                  <div className="invoice-card-actions">
                    <button className="view-btn" onClick={() => handleView(invoice.id)} title="View Invoice">
                      👁️ View
                    </button>
                    <button className="print-btn" onClick={() => handlePrint(invoice)} title="Print Invoice">
                      🖨️ Print
                    </button>
                    <button className="edit-btn" onClick={() => handleEdit(invoice.id)} title="Edit Invoice">
                      ✏️ Edit
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(invoice.id)} title="Delete Invoice">
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item active">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .invoice-list-app {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
          flex-wrap: wrap;
          gap: 10px;
        }

        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        .view-toggle {
          display: flex;
          background: #f0f0f0;
          border-radius: 20px;
          overflow: hidden;
        }

        .toggle-btn {
          padding: 6px 12px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s;
        }

        .toggle-btn.active {
          background: #5a60ea;
          color: white;
        }

        .create-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }

        .financial-year-selector {
          padding: 15px 20px;
          background: white;
          margin: 10px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .financial-year-selector label {
          font-weight: 500;
          color: #666;
        }

        .year-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .invoice-range {
          font-size: 12px;
          color: #5a60ea;
          font-weight: 500;
        }

        /* Search Styles */
        .search-container {
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .search-icon {
          position: absolute;
          left: 12px;
          color: #999;
          font-size: 16px;
        }

        .search-input {
          flex: 1;
          padding: 14px 14px 14px 40px;
          border: 1px solid #ddd;
          border-radius: 12px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }

        .search-input:focus {
          border-color: #5a60ea;
        }

        .clear-search {
          position: absolute;
          right: 10px;
          background: none;
          border: none;
          color: #999;
          font-size: 16px;
          cursor: pointer;
          padding: 5px;
        }

        .search-results-count {
          margin-top: 8px;
          font-size: 13px;
          color: #666;
          text-align: right;
        }

        /* Stats Cards */
        .stats-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          padding: 0 20px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: white;
          padding: 15px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          text-align: center;
        }

        .stat-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }

        .stat-value {
          display: block;
          font-size: 18px;
          font-weight: bold;
          color: #5a60ea;
        }

        /* Table Styles */
        .table-container {
          margin: 0 20px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .table-responsive {
          overflow-x: auto;
        }

        .invoice-table {
          width: 100%;
          border-collapse: collapse;
        }

        .invoice-table th,
        .invoice-table td {
          padding: 12px 10px;
          text-align: left;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
        }

        .invoice-table th {
          background: #fafafa;
          font-weight: 600;
          color: #333;
          position: sticky;
          top: 0;
        }

        .invoice-table .amount {
          text-align: right;
        }

        .serial-no {
          text-align: center;
          width: 50px;
          color: #999;
        }

        .invoice-no {
          font-weight: bold;
          color: #5a60ea;
          font-family: monospace;
        }

        .customer-name {
          font-weight: 500;
          max-width: 150px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .grand-total {
          font-weight: bold;
          color: #28a745;
        }

        .status-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 500;
        }

        .status-badge.completed {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status-badge.pending {
          background: #fff3e0;
          color: #f57c00;
        }

        .status-badge.cancelled {
          background: #ffebee;
          color: #dc3545;
        }

        .actions {
          white-space: nowrap;
          text-align: center;
        }

        .action-btn {
          padding: 5px 8px;
          margin: 0 2px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
          background: transparent;
        }

        .action-btn.view { color: #5a60ea; }
        .action-btn.print { color: #28a745; }
        .action-btn.edit { color: #ff9800; }
        .action-btn.delete { color: #dc3545; }

        .action-btn:hover {
          background: #f0f0f0;
          transform: scale(1.05);
        }

        .totals-row {
          background: #f5f5f5;
          font-weight: bold;
        }

        /* Card View Styles */
        .invoices-container {
          padding: 0 20px;
        }

        .empty-state {
          background: white;
          border-radius: 12px;
          padding: 40px 20px;
          text-align: center;
          color: #666;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .create-first-btn, .clear-search-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 20px;
          margin-top: 15px;
          cursor: pointer;
        }

        .invoice-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 15px;
        }

        @media (min-width: 768px) {
          .invoice-cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .invoice-cards {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .invoice-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }

        .invoice-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }

        .invoice-card-header {
          background: #f8f9fa;
          padding: 12px 15px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .invoice-card-body {
          padding: 15px;
        }

        .customer-info strong {
          display: block;
          font-size: 16px;
          margin-bottom: 3px;
        }

        .customer-info span {
          font-size: 12px;
          color: #666;
        }

        .invoice-details {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #666;
          margin: 10px 0;
        }

        .items-count {
          font-size: 12px;
          color: #888;
          border-top: 1px dashed #eee;
          padding-top: 8px;
        }

        .invoice-card-actions {
          display: flex;
          border-top: 1px solid #eee;
        }

        .invoice-card-actions button {
          flex: 1;
          padding: 10px;
          border: none;
          background: white;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.2s;
        }

        .invoice-card-actions button:hover {
          background: #f5f5f5;
        }

        .view-btn { color: #5a60ea; }
        .print-btn { color: #28a745; border-left: 1px solid #eee; border-right: 1px solid #eee; }
        .edit-btn { color: #ff9800; border-right: 1px solid #eee; }
        .delete-btn { color: #dc3545; }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: #f5f5f5;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f3f3;
          border-top-color: #5a60ea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav .nav-item {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }

        .bottom-nav .nav-item.active {
          color: #5a60ea;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}